import React, {useCallback, useMemo} from 'react';
import {Slider, Box, SliderThumb, Typography} from '@mui/material';
import './ArrSlider.scss';
import ArrNumericInput from '../arr-numeric-input/ArrNumericInput';
import {ReactComponent as SliderThumbIcon} from '../../../../src/assets/images/slider-thumb.svg';
import {ReactComponent as SliderThumbDisabledIcon} from '../../../../src/assets/images/slider-thumb-disabled.svg';

export interface SliderInputProps {
  value: number;
  onChange: (newValue: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  ariaLabel?: string;
  label?: string;
  showInput?: boolean;
  dataTestId: string;
}

interface ThumbComponentProps extends React.HTMLAttributes<unknown> {
  disabled: boolean;
}

const createThumbComponent = (disabled: boolean) => {
  return function Thumb(props: ThumbComponentProps) {
    const {children, ...other} = props;
    return (
      <SliderThumb {...other}>
        {children}
        {disabled ? <SliderThumbDisabledIcon /> : <SliderThumbIcon />}
      </SliderThumb>
    );
  };
};

const ArrSlider: React.FC<SliderInputProps> = ({
  value,
  onChange,
  min = 1,
  max = 100,
  step = 1,
  disabled = false,
  ariaLabel,
  label,
  showInput = true,
  dataTestId,
}) => {
  const clamp = useCallback(
    (val: number): number => Math.max(min, Math.min(max, val)),
    [min, max]
  );

  const handleSliderChange = useCallback(
    (_: Event, newValue: number | number[]) => {
      if (typeof newValue === 'number') {
        onChange(clamp(newValue));
      }
    },
    [clamp, onChange]
  );

  const ThumbComponent = useMemo(
    () => createThumbComponent(disabled),
    [disabled]
  );

  return (
    <Box className="arr-slider-root">
      {label && (
        <Typography
          className="arr-slider-label"
          data-testid={`arr-slider-label-${dataTestId}`}
        >
          {label}
        </Typography>
      )}
      {showInput && (
        <ArrNumericInput
          disabled={disabled}
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          ariaLabel={ariaLabel}
          dataTestId={`${dataTestId}-numeric-input`}
        />
      )}
      <Slider
        disabled={disabled}
        className="MuiSlider"
        slots={{thumb: ThumbComponent}}
        aria-label={ariaLabel}
        value={value}
        onChange={handleSliderChange}
        min={min}
        max={max}
        size={'small'}
        step={step}
        data-testid="slider"
      />
    </Box>
  );
};

export default ArrSlider;
